import { readCurrentItem } from "./method.js";
import type {
  CompiledValue,
  ExpressionScopeCacheNode,
  RuntimeContext,
} from "./types.js";

// Bound the transient cache even for pairwise predicates on very large rosters.
const MAX_SCOPE_RESULTS = 128;
const NEGATIVE_ZERO = Symbol("negative zero");
const ROW_CONTEXT_SOURCES = new Set([
  "athlete",
  "currentAthlete",
  "referenceItem",
  "layoutListRowItem",
  "iterationItem",
  "iterationItemA",
  "iterationItemB",
]);

/** Share pure subexpressions between list iterations in one immutable pass.
 * Keys include the effective item of each free list, including outer scopes.
 * The holder is discarded between passes and on a runtime-variable write. */
export function evaluateScopedExpression(
  expression: CompiledValue,
  ctx: RuntimeContext,
): unknown {
  const cache = ctx.__expressionCache;
  if (!cache) return expression.evaluate(ctx);
  let values = cache.byContext.get(ctx);
  if (!values) {
    values = new Map();
    cache.byContext.set(ctx, values);
  }
  if (values.has(expression)) return values.get(expression);

  let leaf: ExpressionScopeCacheNode | undefined;
  let entries;
  const scope = expression.evaluationScope;
  const inIteration =
    !!ctx.__iterationScopes?.length ||
    ctx.iterationItem !== undefined ||
    ctx.iterationItemA !== undefined ||
    ctx.iterationItemB !== undefined;
  // Cache list queries, loop invariants and costly per-item calculations. Cheap
  // reads and expressions comparing two rows rarely repeat across contexts.
  const costlyItem =
    cache.batch &&
    scope &&
    scope.items.length > 0 &&
    scope.cost >= 16 &&
    !scope.sources.some((source) => ROW_CONTEXT_SOURCES.has(source));
  if (
    scope &&
    (scope.query || costlyItem || (scope.items.length === 0 && inIteration))
  ) {
    cache.byScope ??= new WeakMap();
    entries = cache.byScope.get(expression);
    if (!entries) {
      entries = { root: {}, size: 0 };
      cache.byScope.set(expression, entries);
    }
    leaf = entries.root;
    let keys: unknown[];
    try {
      keys = [
        ...scope.sources.map((source) => ctx[source as keyof RuntimeContext]),
        ...scope.items.map((list) =>
          readCurrentItem(ctx, undefined, list.evaluate(ctx)),
        ),
      ];
    } catch {
      // A free item can belong to an unused lazy branch. If resolving its list
      // fails, keep the original evaluator's branch/error semantics.
      const value = expression.evaluate(ctx);
      values.set(expression, value);
      return value;
    }
    for (const value of keys) {
      if (!leaf) break;
      // Map uses SameValueZero, while pure expressions can distinguish -0.
      const key = Object.is(value, -0) ? NEGATIVE_ZERO : value;
      let next: ExpressionScopeCacheNode | undefined = leaf.children?.get(key);
      if (!next) {
        if (entries.size >= MAX_SCOPE_RESULTS) {
          leaf = undefined;
          break;
        }
        next = {};
        leaf.children ??= new Map();
        leaf.children.set(key, next);
      }
      leaf = next;
    }
    if (leaf?.result) {
      values.set(expression, leaf.result.value);
      return leaf.result.value;
    }
  }

  const value = expression.evaluate(ctx);
  values.set(expression, value);
  if (leaf && entries && entries.size < MAX_SCOPE_RESULTS) {
    leaf.result = { value };
    entries.size++;
  }
  return value;
}
